// Library imports
import React, { useEffect, useRef, useState } from "react";

// User Component imports
import { PROMPT_GROUPS, verLabel, buildVersionRows, DRAFT_VERSION, MESSAGE_MAX_LENGTH } from "../utils/promptVersions";
import { layoutBranches, lineageOf, graphWindow } from "../utils/versionGraph";
import { shortcutLabel, isSubmitShortcut } from "../utils/shortcut";

// ブランチ図の寸法（レーン幅・見せるレーン数・点の縦位置・図の高さ＝展開時の最小行高）
const LANE = 14;
const VISIBLE_LANES = 2;
const DOT_Y = 17;
const GRAPH_H = 92;
// メモが複数行で行が伸びても次の行へつながるよう、縦線は行より長く引く（はみ出しは行で切る）
const LINE_END = 400;

const laneX = (x) => x * LANE + LANE / 2;

// 1行分のブランチ図。選択中の版の系譜（祖先）を強調する
const BranchGraph = ({ row, g, laneCount, isSel, lineage }) => {
    const inLine = !!lineage[row.v];
    const width = laneCount * LANE;
    const lines = [];
    const line = (key, d, highlight, dashed) => lines.push(
        <path key={key} d={d} className={(highlight ? "is-hl" : "") + (dashed ? " is-dashed" : "")} />
    );
    g.through.forEach((t) => line("t" + t.lane, `M${laneX(t.lane)} 0V${LINE_END}`, !!lineage[t.v] && !!lineage[t.child], false));
    if (g.fromAbove) line("a", `M${laneX(g.lane)} 0V${DOT_Y}`, inLine && !!lineage[g.fromAboveChild], g.fromAboveDraft);
    g.joins.forEach((j) => line("j" + j.lane,
        `M${laneX(j.lane)} 0C${laneX(j.lane)} ${DOT_Y * 0.7} ${laneX(g.lane)} ${DOT_Y * 0.3} ${laneX(g.lane)} ${DOT_Y}`,
        inLine && !!lineage[j.child], j.draft));
    if (row.parent !== null) line("b", `M${laneX(g.lane)} ${DOT_Y}V${LINE_END}`, inLine && !!lineage[row.parent], row.draft);

    const dotClass = "ops-ver-graph-dot"
        + (row.draft ? " is-draft" : "")
        + (row.isDeployed || isSel ? " is-filled" : (inLine ? " is-lineage" : ""));
    return (
        <svg className="ops-ver-graph" width={width} height={GRAPH_H} viewBox={`0 0 ${width} ${GRAPH_H}`}>
            {lines}
            <circle className={dotClass} cx={laneX(g.lane)} cy={DOT_Y} r={isSel ? 5.5 : 4.5} />
        </svg>
    );
};

// 変更メモのその場編集（Enter は改行、Ctrl+Enter で保存、Esc で取り消し）。保存に失敗したら入力を残して続けられる
const MemoEditor = ({ initial, onSubmit, onCancel }) => {
    const [value, setValue] = useState(initial);
    const [busy, setBusy] = useState(false);
    const tooLong = value.length > MESSAGE_MAX_LENGTH;

    const submit = async () => {
        if (busy || tooLong) return;
        setBusy(true);
        if (!(await onSubmit(value.trim()))) setBusy(false);
    };
    const onKeyDown = (e) => {
        if (e.nativeEvent.isComposing) return;
        if (isSubmitShortcut(e)) {
            e.preventDefault();
            submit();
        } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
        }
    };
    // 入力欄を開いたら末尾にカーソルを置く
    const inputRef = useRef(null);
    useEffect(() => {
        const el = inputRef.current;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
    }, []);

    return (
        <div className="ops-ver-memo-form">
            <textarea ref={inputRef} rows={1} className={"ops-ver-memo-input" + (tooLong ? " is-error" : "")} value={value} maxLength={MESSAGE_MAX_LENGTH + 1}
                disabled={busy} placeholder="変更メモ" aria-label="変更メモ"
                title={tooLong ? `変更メモは${MESSAGE_MAX_LENGTH}文字以内で入力してください` : ""}
                onChange={(e) => setValue(e.target.value)} onKeyDown={onKeyDown} />
            <button type="button" className="ops-ver-memo-btn is-ok" title={`保存（${shortcutLabel("Enter")}）`} disabled={busy || tooLong} onClick={submit}>
                <i className="bi bi-check-lg" />
            </button>
            <button type="button" className="ops-ver-memo-btn" title="取り消す（Esc）" disabled={busy} onClick={onCancel}>
                <i className="bi bi-x-lg" />
            </button>
        </div>
    );
};

/**
 * プロンプト管理の左カラム（バージョン履歴）
 * 開閉は幅・行高・透明度のトランジションだけで行い、要素の表示切替でレイアウトを飛ばさない
 */
const PromptVersionPanel = ({ groupKey, groups, collapsed, onToggleCollapsed, isUnsaved, draft, errCount, currentV, onSelectGroup, onSave, onLoad, onDiff, onApply, onToggleStar, onSaveMessage }) => {
    // 変更メモを編集中の版（一度に1行だけ）
    const [editingV, setEditingV] = useState(null);
    useEffect(() => setEditingV(null), [groupKey, collapsed]);

    const submitMessage = async (r, message) => {
        if (message === (r.message || "")) {
            setEditingV(null);
            return true;
        }
        const ok = await onSaveMessage(r.v, message);
        if (ok) setEditingV(null);
        return ok;
    };

    const group = groups[groupKey];
    const rows = buildVersionRows(group.versions, draft);
    const canSave = isUnsaved(groupKey) && !errCount;
    const saveTitle = errCount
        ? `エラーがあるため保存できません（${errCount}件）`
        : (isUnsaved(groupKey) ? "現在の編集内容を新しいバージョンとして保存します" : "未保存の編集はありません");

    // エディタに表示している版（未保存の編集があれば「編集中」の行）を強調する
    const selV = currentV;

    const graph = layoutBranches(rows);
    const selIdx = rows.findIndex((r) => r.v === selV);
    const view = graphWindow(selIdx >= 0 ? graph.rows[selIdx].lane : 0, graph.laneCount, VISIBLE_LANES);
    const lineage = lineageOf(rows, selV);

    return (
        <div className={"ops-ver-panel" + (collapsed ? " is-collapsed" : "")}>
            <div className="ops-ver-head">
                <button type="button" className="ops-ver-toggle" onClick={onToggleCollapsed} title="クリックで履歴パネルを開閉します">
                    <i className="bi bi-chevron-right ops-ver-caret" />
                    <span className="ops-ver-title">バージョン</span>
                </button>
                <button type="button" className="ops-pill ops-ver-fade" title={`${saveTitle}（${shortcutLabel("S")}）`} aria-disabled={!canSave} onClick={() => canSave && onSave()}>
                    <i className="bi bi-save" /><span>保存</span>
                </button>
            </div>

            <div className="ops-ver-summary">
                <span className="ops-ver-summary-label">適用中</span>
                {PROMPT_GROUPS.map((g) => {
                    const item = groups[g.key];
                    const current = g.key === groupKey;
                    const dirty = isUnsaved(g.key);
                    return (
                        <button key={g.key} type="button" className={"ops-ver-chip" + (current ? " is-current" : "")} onClick={() => onSelectGroup(g.key)}
                            title={`${g.name}：適用中 ${verLabel(item?.deployed_v, g.key)}` + (dirty ? "（未保存の編集あり）" : "")}>
                            <span className="ops-ver-chip-label">{verLabel(item?.deployed_v, g.key)}</span>
                            {dirty && <span className="ops-ver-chip-dot" title="未保存の編集あり" />}
                        </button>
                    );
                })}
            </div>

            <div className="ops-ver-list">
                {rows.map((r, i) => {
                    const label = r.draft ? "編集中" : verLabel(r.v, groupKey);
                    const isSel = r.v === selV;
                    const applyDisabled = r.isDeployed || (r.draft && errCount > 0);
                    const applyTitle = r.isDeployed ? "このバージョンは既に適用中です"
                        : (r.draft && errCount) ? `エラーがあるため適用できません（${errCount}件）`
                        : (r.draft ? "編集中の内容を保存して適用します" : "このバージョンを適用します");
                    return (
                        <div key={r.v} data-ver-row={r.v}
                            className={"ops-ver-row" + (isSel ? " is-selected" : "") + (r.isDeployed ? " is-deployed" : "")}
                            title={collapsed ? [label, r.date, r.message].join(" / ") : ""}>
                            <div className="ops-ver-gutter" style={{ width: view.visLanes * LANE + 2 }}>
                                <div className="ops-ver-gutter-inner" style={{ transform: `translateX(${-view.shift * LANE}px)` }}>
                                    <BranchGraph row={r} g={graph.rows[i]} laneCount={graph.laneCount} isSel={isSel} lineage={lineage} />
                                </div>
                            </div>
                            <div className="ops-ver-content">
                                <div className="ops-ver-line1">
                                    <span className="ops-ver-label">{label}</span>
                                    {!r.draft && (!collapsed || r.isStarred) &&
                                        <button type="button" className={"ops-ver-star" + (r.isStarred ? " is-on" : "")}
                                            title={r.isStarred ? "スターを外す" : "良かったバージョンとしてスターを付ける"}
                                            onClick={() => onToggleStar(r.v, !r.isStarred)}>
                                            <i className={r.isStarred ? "bi bi-star-fill" : "bi bi-star"} />
                                        </button>
                                    }
                                    <span className="ops-ver-meta ops-ver-fade">
                                        {r.isDeployed && <span className="ops-ver-tag is-deployed">適用中</span>}
                                        {r.draft && <span className="ops-ver-tag is-draft">未保存</span>}
                                        <span className="ops-ver-spacer" />
                                        <span className="ops-ver-date" title={r.date}>{r.date}</span>
                                    </span>
                                </div>
                                <div className="ops-ver-line2 ops-ver-fade">
                                    {editingV === r.v ?
                                        <MemoEditor initial={r.message || ""} onSubmit={(message) => submitMessage(r, message)} onCancel={() => setEditingV(null)} />
                                        :
                                        <>
                                            <div className={"ops-ver-message" + (r.draft ? "" : " is-editable")}
                                                title={r.draft || collapsed ? "" : "(ダブルクリックで編集)" + (r.message ? "\n\n" + r.message : "")}
                                                onDoubleClick={() => !r.draft && !collapsed && setEditingV(r.v)}>
                                                {r.message || "（メモなし）"}
                                            </div>
                                            {!r.draft && !collapsed &&
                                                <button type="button" className="ops-ver-memo-edit" title="変更メモを編集します" onClick={() => setEditingV(r.v)}>
                                                    <i className="bi bi-pencil" />
                                                </button>
                                            }
                                        </>
                                    }
                                </div>
                                <div className="ops-ver-actions ops-ver-fade">
                                    <button type="button" className="ops-ver-action" aria-disabled={r.draft} title={r.draft ? "編集中の内容です" : "このバージョンをエディタへ展開します"}
                                        onClick={() => !r.draft && onLoad(r.v)}><i className="bi bi-pencil-square" /><span>読み込み</span></button>
                                    {/* 適用中の版は適用中の内容そのものなので差分が出ない */}
                                    <button type="button" className="ops-ver-action" aria-disabled={r.isDeployed}
                                        title={r.isDeployed ? "適用中のバージョンです（差分はありません）" : "適用中の内容との差分を表示します"}
                                        onClick={() => !r.isDeployed && onDiff(r.v === DRAFT_VERSION ? null : r.v)}><i className="bi bi-file-diff" /><span>差分</span></button>
                                    <span className="ops-ver-spacer" />
                                    <button type="button" className="ops-ver-action ops-ver-apply" aria-disabled={applyDisabled} title={applyTitle}
                                        onClick={() => !applyDisabled && onApply(r.v === DRAFT_VERSION ? null : r.v)}><i className="bi bi-check2-circle" /><span>適用</span></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {!rows.length && <div className="ops-ver-empty">バージョンはまだありません</div>}
            </div>
        </div>
    );
};

export default PromptVersionPanel;
